import { useMemo } from 'react'
import {
  Box,
  Text,
  Flex,
  VStack,
  Link,
  Heading,
  List,
  ListItem,
  ListIcon,
} from '@ritualco/jutsu'
import IdentityProviderButton from 'components/IdentityProviderButton'
import { IdentityProvider } from 'types/entities/IdentityProvider'
import { useContent } from 'lib/content/react'
import { useAnalytics } from 'lib/simple-analytics'
import { ReactComponent as RitualLogo } from 'assets/Images/Ritual-Logo-Unpadded.svg'
import { ReactComponent as Shopping } from 'assets/Images/Shopping.svg'
import { MdCheckCircle } from 'react-icons/md'

const columnStyling = {
  w: { md: '50%' },
  p: { base: 5, md: 0 },
}

const contentMiddleSideStyling = { base: 'auto', md: '40px', lg: '124px' }
const contentEdgeSideStyling = 'auto'

export type InviteMemberLoginProps = {
  identityProviders: Array<IdentityProvider>
  onLogin?: (provider: IdentityProvider) => void
  onRegister?: () => void
  organizationName: string
}

const InviteMemberLogin = ({
  identityProviders,
  onLogin = () => {},
  onRegister = () => {},
  organizationName,
}: InviteMemberLoginProps) => {
  const { track } = useAnalytics()
  const { formatMessage } = useContent()
  const emailProvider = useMemo(() => {
    return identityProviders.filter(provider => provider.id === 'email')[0]
  }, [identityProviders])

  return (
    <Flex
      minH="100%"
      flexDir={{ base: 'column-reverse', md: 'row' }}
      justifyContent="flex-end"
    >
      {/* Value Props */}
      <Flex
        bg="teal.light"
        {...columnStyling}
        pb="0"
        flex={{ base: '1 0 auto', md: '0 0 auto' }}
      >
        <Flex
          maxW="319px"
          my="auto"
          ml={contentEdgeSideStyling}
          mr={contentMiddleSideStyling}
          flexDir={{ base: 'column-reverse', md: 'column' }}
        >
          <Flex justifyContent="center" mb={{ md: '50' }}>
            <Shopping />
          </Flex>
          <Box>
            <Heading
              as="h1"
              fontSize="xlarge"
              textAlign={{ md: 'center' }}
              mb="7"
            >
              {formatMessage(
                { id: 'pmportal.invites.joinTitle' },
                { organizationName }
              )}
            </Heading>
            <List>
              {formatMessage({ id: 'pmportal.invites.members.valueProps' })
                .split('\n')
                .map(line => (
                  <ListItem display="flex" flexDir="row" mb="10px">
                    <ListIcon as={MdCheckCircle} color="#92e5ff" mt={1} />
                    <Text display="inline" fontSize="medium">
                      {line}
                    </Text>
                  </ListItem>
                ))}
            </List>
          </Box>
        </Flex>
      </Flex>
      {/* Login */}
      <Flex {...columnStyling}>
        <Flex
          maxW={{ md: '319px', lg: '376px' }}
          my="auto"
          mr={contentEdgeSideStyling}
          ml={contentMiddleSideStyling}
          flexDir="column"
        >
          <Box h="3" mb="5">
            <RitualLogo height="100%" />
          </Box>
          <Text>
            {formatMessage(
              { id: 'pmportal.invites.signupPrompt' },
              { organizationName }
            )}
          </Text>
          <Text>
            {formatMessage(
              { id: 'pmportal.invites.loginPrompt' },
              {
                link: text => (
                  <Link
                    fontWeight="bold"
                    onClick={() => onLogin(emailProvider)}
                  >
                    {text}
                  </Link>
                ),
              }
            )}
          </Text>
          <VStack spacing={3} w="100%" mt={8} mb={8}>
            {identityProviders.map(provider => (
              <IdentityProviderButton
                key={provider.id}
                h="3.28em"
                fontWeight="medium"
                {...provider}
                onClick={() => {
                  if (provider.id === 'email') {
                    onRegister()
                  } else {
                    onLogin(provider)
                  }

                  track(provider.clickEvent)
                }}
              />
            ))}
          </VStack>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default InviteMemberLogin
